import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Mail,
  MessageSquare,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { FilterChip } from '@/components/ui/Chip';
import { Input } from '@/components/ui/Input';
import { PatientTabEmpty, PatientTabFrame, PatientTabToolbar } from '@/components/patient/PatientTabFrame';
import { formatActivityDayHeading } from '@/lib/patientActivity';
import { MessageAttachments } from '@/components/messages/MessageAttachments';
import { MessageSenderHeading } from '@/components/messages/MessageSenderHeading';
import { CommunicationCompose } from '@/components/patient/CommunicationCompose';
import { EmailDeliveryPill, PatientEmailThreads } from '@/components/patient/PatientEmailThreads';
import { usePatient, useUpdatePatient } from '@/hooks/usePatients';
import { useClinic } from '@/hooks/useClinicSettings';
import { usePatientEmailHistory, type PatientEmailHistoryItem } from '@/hooks/usePatientEmailHistory';
import {
  usePatientEmailThreads,
  usePatientEmailThreadsRealtime,
} from '@/hooks/usePatientEmailThreads';
import { useMarkMessageRead, usePatientMessages, type PatientMessage } from '@/hooks/usePatientMessages';
import {
  usePatientSmsLogs,
  usePatientSmsLogsRealtime,
  useSmsClinic,
  type SmsLogRow,
  type SmsLogStatus,
} from '@/hooks/useSms';
import {
  PatientEmailHistoryDetails,
  PatientEmailTypeBadge,
} from '@/components/email/PatientEmailHistoryBits';
import {
  communicationChannelCounts,
  communicationFilterFromParam,
  filterCommunicationTimeline,
  mergeCommunicationTimeline,
  type CommunicationChannel,
  type CommunicationDirection,
  type CommunicationFilter,
  type CommunicationTimelineEntry,
} from '@/lib/patientCommunication';
import {
  patientTelephoneNumbers,
  primaryTelephoneNumber,
} from '@/lib/patientStamkortFields';
import { smsPriceDkk, smsTriggerLabelKey } from '@/lib/smsCompose';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  formatMessageDateTime,
  personDisplayName,
  resolveMessageSender,
} from '@/utils/messageSender';
import type { Database } from '@/types/database.generated';

interface CommunicationTabProps {
  clinicId: string;
  patientId: string;
  highlightMessageId?: string | null;
  highlightThreadId?: string | null;
  initialTabParam?: string | null;
  initialChannelParam?: string | null;
  canViewSms: boolean;
  canViewEmail: boolean;
  canViewMessages: boolean;
  canSendSms?: boolean;
  canSendMessages?: boolean;
}

type SmsConsentStatus = Database['public']['Enums']['sms_consent_status'];
type SmsConsent = {
  status?: SmsConsentStatus;
  updated_at?: string;
  source?: string;
};

type TimelineItem =
  | (CommunicationTimelineEntry & { channel: 'sms'; log: SmsLogRow })
  | (CommunicationTimelineEntry & { channel: 'email'; emailKind: 'history'; email: PatientEmailHistoryItem })
  | (CommunicationTimelineEntry & { channel: 'messages'; message: PatientMessage });

const FILTERS: CommunicationFilter[] = ['all', 'sms', 'email', 'messages'];

export function CommunicationTab({
  clinicId,
  patientId,
  highlightMessageId,
  highlightThreadId,
  initialTabParam,
  initialChannelParam,
  canViewSms,
  canViewEmail,
  canViewMessages,
  canSendSms = false,
  canSendMessages = false,
}: CommunicationTabProps) {
  const { t, i18n } = useTranslation();
  const [filter, setFilter] = useState<CommunicationFilter>(() =>
    communicationFilterFromParam(initialTabParam, initialChannelParam),
  );
  const [search, setSearch] = useState('');
  const [replyThreadId, setReplyThreadId] = useState<string | null>(highlightThreadId ?? null);

  const { data: patient, isLoading: patientLoading } = usePatient(patientId, clinicId);
  const { data: clinic } = useSmsClinic(canViewSms ? clinicId : null);
  const { data: clinicRow } = useClinic(canViewEmail ? clinicId : null);
  const { data: threads = [], isLoading: threadsLoading } = usePatientEmailThreads(
    canViewEmail ? clinicId : null,
    canViewEmail ? patientId : null,
  );
  usePatientEmailThreadsRealtime(canViewEmail ? clinicId : null, canViewEmail ? patientId : null);
  const { data: logs = [], isLoading: logsLoading } = usePatientSmsLogs(
    canViewSms ? clinicId : null,
    canViewSms ? patientId : null,
  );
  const { data: emails = [], isLoading: emailsLoading } = usePatientEmailHistory(
    canViewEmail ? clinicId : null,
    canViewEmail ? patientId : null,
  );
  const { data: messages = [], isLoading: messagesLoading } = usePatientMessages(
    canViewMessages ? patientId : null,
    clinicId,
  );
  const markRead = useMarkMessageRead();
  const updatePatient = useUpdatePatient();

  usePatientSmsLogsRealtime(canViewSms ? clinicId : null, canViewSms ? patientId : null);

  useEffect(() => {
    setFilter(communicationFilterFromParam(initialTabParam, initialChannelParam));
  }, [initialTabParam, initialChannelParam]);

  useEffect(() => {
    if (highlightThreadId) setReplyThreadId(highlightThreadId);
  }, [highlightThreadId]);

  useEffect(() => {
    if (!highlightMessageId) return;
    const el = document.getElementById(`patient-message-${highlightMessageId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightMessageId, messages]);

  useEffect(() => {
    if (!highlightThreadId) return;
    const el = document.getElementById(`patient-email-thread-${highlightThreadId}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightThreadId, threads]);

  const consent = (patient?.sms_consent ?? {}) as SmsConsent;
  const consentStatus = consent.status ?? 'not_set';
  const phoneNumbers = patientTelephoneNumbers(patient?.telephone_subscribers);
  const phone = primaryTelephoneNumber(patient?.telephone_subscribers) ?? '';
  const email = patient?.email ?? '';
  const patientName = personDisplayName(patient?.first_name, patient?.last_name);
  const smsEnabled = clinic?.settings?.enabled ?? false;
  const hasPortalAccount = Boolean(patient?.portal_user_id);

  const items = useMemo(() => {
    const next: TimelineItem[] = [];
    if (canViewSms) {
      for (const log of logs) {
        next.push({
          id: `sms:${log.id}`,
          channel: 'sms',
          createdAt: log.created_at,
          direction: log.direction === 'inbound' ? 'inbound' : 'outbound',
          searchText: [log.body, log.to_phone, log.sender, log.patient_name_snapshot, log.trigger]
            .filter(Boolean)
            .join(' '),
          log,
        });
      }
    }
    if (canViewEmail) {
      for (const row of emails) {
        if (row.email_type === 'manual') continue;
        next.push({
          id: `email:${row.id}`,
          channel: 'email',
          emailKind: 'history',
          createdAt: row.created_at,
          direction: 'outbound',
          searchText: [row.subject, row.sent_to, row.sender, row.email_type, row.invoice_number]
            .filter(Boolean)
            .join(' '),
          email: row,
        });
      }
    }
    if (canViewMessages) {
      for (const message of messages) {
        const sender = resolveMessageSender(message, { patientName });
        next.push({
          id: `messages:${message.id}`,
          channel: 'messages',
          createdAt: message.created_at,
          direction: message.sender === 'patient' ? 'inbound' : 'outbound',
          searchText: [message.content, sender.name].filter(Boolean).join(' '),
          message,
        });
      }
    }
    return mergeCommunicationTimeline(next);
  }, [canViewEmail, canViewMessages, canViewSms, emails, logs, messages, patientName]);

  const counts = useMemo(() => {
    const next = communicationChannelCounts(items);
    if (canViewEmail && threads.length > 0) {
      next.email += threads.length;
      next.all += threads.length;
    }
    return next;
  }, [canViewEmail, items, threads.length]);
  const visible = useMemo(
    () => filterCommunicationTimeline(items, filter, search),
    [filter, items, search],
  );

  const updateConsent = async (next: SmsConsentStatus) => {
    try {
      await updatePatient.mutateAsync({
        patient_id: patientId,
        sms_consent: {
          status: next,
          updated_at: new Date().toISOString(),
          source: 'clinic_admin',
        },
      });
      toast.success(
        next === 'accepted'
          ? t('sms.consent_updated_accepted', 'Patienten er tilmeldt SMS-kommunikation.')
          : next === 'revoked'
            ? t('sms.consent_updated_revoked', 'SMS-samtykke er trukket tilbage.')
            : t('sms.consent_updated_declined', 'SMS-samtykke er afvist.'),
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t('sms.consent_update_failed', 'Kunne ikke opdatere samtykke.');
      toast.error(message);
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      await markRead.mutateAsync({ message_id: messageId, patient_id: patientId });
      toast.success(t('messages.marked_read'));
    } catch (err) {
      console.error('Error marking message as read:', err);
      toast.error(t('messages.error_marking_read'));
    }
  };

  const loading =
    patientLoading ||
    (canViewSms && logsLoading) ||
    (canViewEmail && (emailsLoading || threadsLoading)) ||
    (canViewMessages && messagesLoading);

  const groups = groupCommunicationByDay(visible);

  const focusCompose = () => {
    document.getElementById('patient-communication-compose')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
    const field = document.querySelector<HTMLElement>(
      '#patient-communication-compose textarea, #patient-communication-compose input',
    );
    field?.focus();
  };

  return (
    <PatientTabFrame
      title={t('patients.tabs.communication')}
      count={items.length}
      action={
        canSendSms || canSendMessages || canViewEmail ? (
          <Button type="button" onClick={focusCompose}>
            {t('patients.communication.new_message')}
          </Button>
        ) : undefined
      }
      loading={loading}
      toolbar={
        <PatientTabToolbar
          chips={
            <>
              {FILTERS.filter(
                (key) => key === 'all' || channelVisible(key, canViewSms, canViewEmail, canViewMessages),
              ).map((key) => (
                <FilterChip
                  key={key}
                  pressed={filter === key}
                  count={counts[key]}
                  onClick={() => setFilter(key)}
                >
                  {filterLabel(t, key)}
                </FilterChip>
              ))}
            </>
          }
          search={
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('patients.communication.search_placeholder')}
              aria-label={t('patients.communication.search_placeholder')}
            />
          }
        />
      }
    >
      <div id="patient-communication-compose" className="mt-s4">
        <CommunicationCompose
          clinicId={clinicId}
          patientId={patientId}
          patientName={patientName}
          phoneNumbers={phoneNumbers}
          email={email}
          hasPortalAccount={hasPortalAccount}
          consentAccepted={consentStatus === 'accepted'}
          smsEnabled={smsEnabled}
          hasCredit={(clinic?.credits.balance ?? 0) > 0}
          canSms={canSendSms}
          canEmail={canViewEmail}
          canMessages={canSendMessages}
          preferredChannel={filter}
          clinicEmailReady={Boolean(clinicRow?.email_local_part)}
          clinicSignature={clinicRow?.email_signature_text ?? ''}
          threadId={replyThreadId}
          onEmailSent={() => setReplyThreadId(null)}
        />
      </div>

      {canViewEmail && threads.length > 0 ? (
        <PatientEmailThreads
          threads={threads}
          highlightThreadId={highlightThreadId}
          clinicName={clinicRow?.name}
          onReply={(threadId) => {
            setReplyThreadId(threadId);
            document.getElementById('patient-communication-compose')?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }}
        />
      ) : null}

      {!loading && visible.length === 0 && threads.length === 0 ? (
        <PatientTabEmpty
          icon={<MessageSquare className="h-7 w-7" />}
          title={t('patients.communication.empty')}
          description={t('patients.communication.empty_hint')}
        />
      ) : null}

      {visible.length > 0 ? (
        <div className="mt-s4 space-y-s5">
          {groups.map((group) => (
            <div key={group.dayKey}>
              <h3 className={cn(TYPE.label, 'mb-s2 text-ds-muted')}>
                {formatActivityDayHeading(group.dayKey, t, i18n.language)}
              </h3>
              <ul className="divide-y divide-ds-line">
                {group.items.map((item) => (
                  <li key={item.id} className="py-s4">
                    <TimelineRow
                      item={item}
                      language={i18n.language}
                      patientName={patientName}
                      highlightMessageId={highlightMessageId}
                      onMarkRead={handleMarkAsRead}
                      markReadPending={markRead.isPending}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}

      {canViewSms && (
        <SmsConsentSection
          phone={phone}
          smsEnabled={smsEnabled}
          consentStatus={consentStatus}
          updating={updatePatient.isPending}
          onAccept={() => updateConsent('accepted')}
          onRevoke={() => updateConsent('revoked')}
        />
      )}
    </PatientTabFrame>
  );
}

function groupCommunicationByDay(items: TimelineItem[]): Array<{ dayKey: string; items: TimelineItem[] }> {
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const groups: Array<{ dayKey: string; items: TimelineItem[] }> = [];
  const index = new Map<string, TimelineItem[]>();
  for (const item of items) {
    const date = new Date(item.createdAt);
    const isoKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const key = isoKey === todayKey ? 'today' : isoKey;
    const existing = index.get(key);
    if (existing) {
      existing.push(item);
    } else {
      const list = [item];
      index.set(key, list);
      groups.push({ dayKey: key, items: list });
    }
  }
  return groups;
}

function channelVisible(
  channel: CommunicationChannel,
  canViewSms: boolean,
  canViewEmail: boolean,
  canViewMessages: boolean,
): boolean {
  switch (channel) {
    case 'sms':
      return canViewSms;
    case 'email':
      return canViewEmail;
    case 'messages':
      return canViewMessages;
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

function filterLabel(
  t: (key: string, fallback: string) => string,
  filter: CommunicationFilter,
): string {
  switch (filter) {
    case 'all':
      return t('patients.communication.filter_all', 'Alle');
    case 'sms':
      return t('patients.communication.filter_sms', 'SMS');
    case 'email':
      return t('patients.communication.filter_email', 'E-mail');
    case 'messages':
      return t('patients.communication.filter_messages', 'Beskeder');
    default: {
      const _exhaustive: never = filter;
      return _exhaustive;
    }
  }
}

function channelBadge(
  t: (key: string, fallback: string) => string,
  channel: CommunicationChannel,
) {
  switch (channel) {
    case 'sms':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-0.5 text-[11px] font-medium text-ds-teal">
          <MessageSquare className="h-3 w-3" />
          {t('patients.communication.filter_sms', 'SMS')}
        </span>
      );
    case 'email':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-amber-tint px-2 py-0.5 text-[11px] font-medium text-ds-amber-ink">
          <Mail className="h-3 w-3" />
          {t('patients.communication.filter_email', 'E-mail')}
        </span>
      );
    case 'messages':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-0.5 text-[11px] font-medium text-ds-teal">
          <MessageSquare className="h-3 w-3" />
          {t('patients.communication.filter_messages', 'Beskeder')}
        </span>
      );
    default: {
      const _exhaustive: never = channel;
      return _exhaustive;
    }
  }
}

function directionLabel(
  t: (key: string, fallback: string) => string,
  direction: CommunicationDirection,
): string {
  switch (direction) {
    case 'outbound':
      return t('patients.communication.sent', 'Sendt');
    case 'inbound':
      return t('patients.communication.received', 'Modtaget');
    default: {
      const _exhaustive: never = direction;
      return _exhaustive;
    }
  }
}

function TimelineRow({
  item,
  language,
  patientName,
  highlightMessageId,
  onMarkRead,
  markReadPending,
}: {
  item: TimelineItem;
  language: string;
  patientName: string;
  highlightMessageId?: string | null;
  onMarkRead: (messageId: string) => void;
  markReadPending: boolean;
}) {
  const { t } = useTranslation();
  const timestamp = formatMessageDateTime(item.createdAt, language);

  switch (item.channel) {
    case 'sms':
      return (
        <SmsTimelineItem item={item} timestamp={timestamp} directionLabel={directionLabel(t, item.direction)} />
      );
    case 'email':
      return (
        <EmailTimelineItem item={item} timestamp={timestamp} directionLabel={directionLabel(t, item.direction)} />
      );
    case 'messages':
      return (
        <MessageTimelineItem
          item={item}
          language={language}
          patientName={patientName}
          highlight={highlightMessageId === item.message.id}
          onMarkRead={onMarkRead}
          markReadPending={markReadPending}
        />
      );
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
}

function SmsTimelineItem({
  item,
  timestamp,
  directionLabel: dir,
}: {
  item: Extract<TimelineItem, { channel: 'sms' }>;
  timestamp: string;
  directionLabel: string;
}) {
  const { t } = useTranslation();
  const trigger = smsTriggerLabelKey(item.log.trigger);
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          {channelBadge(t, 'sms')}
          <span className="text-[11px] text-ds-muted">{dir}</span>
          <SmsStatusPill status={item.log.status} />
          <span className="text-[11px] text-ds-muted">
            {trigger.key ? t(trigger.key, trigger.fallback) : trigger.fallback}
          </span>
          <span className="text-[11px] text-ds-placeholder">
            {item.direction === 'inbound' ? '←' : '→'} {item.log.to_phone || item.log.sender || '—'}
          </span>
        </div>
        <div className="whitespace-pre-wrap break-words text-sm leading-relaxed text-ds-text-2">
          {item.log.body}
        </div>
        {item.log.error_message && (
          <div className="mt-1 text-[11px] text-ds-red-ink">{item.log.error_message}</div>
        )}
      </div>
      <div className="flex-shrink-0 space-y-0.5 text-right text-[11px] text-ds-muted">
        <div>{timestamp}</div>
        <div className="font-mono">
          {item.log.parts ?? 1} seg · {smsPriceDkk(item.log.parts ?? 1, item.log.clinic_price_cents)} DKK
        </div>
      </div>
    </div>
  );
}

function EmailTimelineItem({
  item,
  timestamp,
  directionLabel: dir,
}: {
  item: Extract<TimelineItem, { channel: 'email'; emailKind: 'history' }>;
  timestamp: string;
  directionLabel: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const email = item.email;
  return (
    <div>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            {channelBadge(t, 'email')}
            <span className="text-[11px] text-ds-muted">{dir}</span>
            <EmailDeliveryPill status={email.status} />
            <PatientEmailTypeBadge emailType={email.email_type} />
            {email.invoice_number && (
              <span className="text-[11px] text-ds-muted">#{email.invoice_number}</span>
            )}
          </div>
          <div className="mb-1 text-sm font-medium text-ds-text">{email.subject}</div>
          <div className="text-xs text-ds-muted">
            {t('email.sent_to', 'Til')}: {email.sent_to}
            {email.sender ? ` · ${email.sender}` : ''}
          </div>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="mt-2 inline-flex items-center gap-1 text-[11px] text-ds-teal hover:text-ds-teal-700"
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {open
              ? t('patients.communication.hide_email_body', 'Skjul detaljer')
              : t('patients.communication.show_email_body', 'Vis detaljer')}
          </button>
          {open ? (
            <div className="mt-2">
              <PatientEmailHistoryDetails email={email} timestamp={timestamp} />
            </div>
          ) : null}
        </div>
        <div className="flex-shrink-0 text-right text-[11px] text-ds-muted">{timestamp}</div>
      </div>
    </div>
  );
}

function MessageTimelineItem({
  item,
  language,
  patientName,
  highlight,
  onMarkRead,
  markReadPending,
}: {
  item: Extract<TimelineItem, { channel: 'messages' }>;
  language: string;
  patientName: string;
  highlight: boolean;
  onMarkRead: (messageId: string) => void;
  markReadPending: boolean;
}) {
  const { t } = useTranslation();
  const msg = item.message;
  const isFromPatient = msg.sender === 'patient';
  const isUnread = !msg.read_at && isFromPatient;
  const sender = resolveMessageSender(msg, { patientName });
  return (
    <div
      id={`patient-message-${msg.id}`}
      className={cn(
        '-mx-2 rounded-lg p-2',
        isUnread && 'bg-ds-teal-tint',
        highlight && 'ring-2 ring-ds-teal',
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {channelBadge(t, 'messages')}
            <span className="text-[11px] text-ds-muted">
              {directionLabel(t, item.direction)}
            </span>
          </div>
          <MessageSenderHeading sender={sender} timestamp={formatMessageDateTime(msg.created_at, language)} />
        </div>
        {isUnread && (
          <Button type="button" onClick={() => onMarkRead(msg.id)} disabled={markReadPending}>
            <CheckCircle />
            {t('messages.mark_read')}
          </Button>
        )}
      </div>
      <div className="whitespace-pre-wrap text-sm text-ds-text-2">{msg.content}</div>
      <MessageAttachments messageId={msg.id} />
      {msg.read_at && !isFromPatient && (
        <p className="mt-2 text-xs text-ds-muted">
          {t('messages.read_at')} {formatMessageDateTime(msg.read_at, language)}
        </p>
      )}
    </div>
  );
}

function SmsStatusPill({ status }: { status: SmsLogStatus | null | undefined }) {
  const { t } = useTranslation();
  switch (status) {
    case 'delivered':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-green-tint px-2 py-0.5 text-[11px] font-medium text-ds-green-ink">
          <CheckCircle2 className="h-3 w-3" />
          {t('sms.status_delivered', 'Leveret')}
        </span>
      );
    case 'sent':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-teal-tint px-2 py-0.5 text-[11px] font-medium text-ds-teal">
          <Clock className="h-3 w-3" />
          {t('sms.status_sent', 'Sendt')}
        </span>
      );
    case 'queued':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-amber-tint px-2 py-0.5 text-[11px] font-medium text-ds-amber-ink">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('sms.status_queued', 'I kø')}
        </span>
      );
    case 'failed':
    case 'rejected':
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-ds-red-tint px-2 py-0.5 text-[11px] font-medium text-ds-red-ink">
          <AlertTriangle className="h-3 w-3" />
          {t('sms.status_failed', 'Fejlet')}
        </span>
      );
    case 'unknown':
    case null:
    case undefined:
      return (
        <span className="inline-flex items-center rounded-full bg-ds-neutral-tint px-2 py-0.5 text-[11px] text-ds-muted">
          —
        </span>
      );
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function SmsConsentSection({
  phone,
  smsEnabled,
  consentStatus,
  updating,
  onAccept,
  onRevoke,
}: {
  phone: string;
  smsEnabled: boolean;
  consentStatus: SmsConsentStatus;
  updating: boolean;
  onAccept: () => void;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="mt-s5 border-t border-ds-line pt-s4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className={cn(TYPE.label, 'text-ds-text')}>
            {t('patients.communication.sms_section', 'SMS')}
          </div>
          <div className="mt-1 text-xs text-ds-muted">
            {phone ? (
              phone
            ) : (
              <span className="text-ds-red-ink">{t('sms.no_phone_registered', 'Intet telefonnummer registreret')}</span>
            )}
          </div>
          {!smsEnabled && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-ds-amber-tint p-3 text-xs text-ds-amber-ink ring-1 ring-ds-amber-line">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div>
                <div className="font-medium">{t('sms.not_enabled_title', 'SMS er ikke aktiveret for denne klinik')}</div>
                <div className="mt-1">
                  {t('sms.not_enabled_hint', 'Du kan aktivere SMS under Indstillinger → Integrationer → SMS.')}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConsentBadge status={consentStatus} />
          {consentStatus !== 'accepted' && (
            <Button variant="secondary" size="sm" onClick={onAccept} disabled={updating || !phone}>
              {updating ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <ShieldCheck className="mr-1.5 h-3 w-3" />}
              {t('sms.mark_as_accepted', 'Marker som tilmeldt')}
            </Button>
          )}
          {consentStatus === 'accepted' && (
            <Button variant="ghost" size="sm" onClick={onRevoke} disabled={updating}>
              {updating ? <Loader2 className="mr-1.5 h-3 w-3 animate-spin" /> : <ShieldOff className="mr-1.5 h-3 w-3" />}
              {t('sms.revoke_consent', 'Træk samtykke tilbage')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ConsentBadge({ status }: { status: SmsConsentStatus | undefined }) {
  const { t } = useTranslation();
  switch (status) {
    case 'accepted':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-green-tint px-3 py-1 text-xs font-medium text-ds-green-ink">
          <ShieldCheck className="h-3 w-3" />
          {t('sms.consent_accepted', 'Tilmeldt SMS')}
        </span>
      );
    case 'revoked':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-neutral-tint px-3 py-1 text-xs font-medium text-ds-muted">
          <ShieldOff className="h-3 w-3" />
          {t('sms.consent_revoked', 'Tilbagekaldt')}
        </span>
      );
    case 'declined':
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-red-tint px-3 py-1 text-xs font-medium text-ds-red-ink">
          <ShieldOff className="h-3 w-3" />
          {t('sms.consent_declined', 'Afvist')}
        </span>
      );
    case 'not_set':
    case undefined:
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ds-amber-tint px-3 py-1 text-xs font-medium text-ds-amber-ink">
          <ShieldAlert className="h-3 w-3" />
          {t('sms.consent_not_set', 'Ikke besluttet')}
        </span>
      );
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}
